<template>
  <div class="flex-container">
    <!-- 顶部面包屑 -->
    <div class="flex-header">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/sys/workflow/workflowList' }">流程列表</el-breadcrumb-item>
        <el-breadcrumb-item>{{ workflow && workflow.channelName || '流程详情' }}</el-breadcrumb-item>
      </el-breadcrumb>
      <div class="channel-summary" v-if="workflow.channelId">
        <div class="summary-left">
          <h2 class="channel-name">{{ workflow.channelName }}
            <el-tag type="tagType" size="large" style="margin-left:12px;vertical-align:middle;">
              {{ wfTypeMap[workflow.wfType] || '-' }}
            </el-tag>
          </h2>
          <div class="channel-meta">
            <span>统一信用代码：{{ workflow.creditCode || '-' }}</span>
            <span>销售模式：{{ workflow.saleModel === '0' ? '线上' : '线下' }}</span>
          </div>
        </div>
        <div class="summary-right">
          <el-button size="medium" @click="goBack"><i class="el-icon-back"></i> 返回列表</el-button>
        </div>
      </div>
    </div>

    <!-- 主体左右布局 -->
    <div class="flex-body">
      <!-- 左侧流程流水线列表 -->
      <div class="sidebar" v-loading="loading.workflow">
        <div class="sidebar-title">流程流水</div>
        <div class="sidebar-list">
          <el-timeline>
            <el-timeline-item
              v-for="wf in pipelineList"
              :key="wf.id"
              :timestamp="wf.createTime"
              :color="wf.id === currentFlowId ? '#409EFF' : (wf.wfState === '4' || wf.wfState === '7' ? '#F56C6C' : '#c0c4cc')"
              :size="wf.id === currentFlowId ? 'large' : 'normal'"
              :hollow="wf.id !== currentFlowId"
              placement="top"
            >
              <div
                class="pipeline-node"
                :class="{ 'is-current': wf.id === currentFlowId }"
                @click="selectFlow(wf.id)"
              >
                <div class="pipeline-node-top">
                  <el-tag type="info" size="mini" class="pipeline-id-tag">流水号</el-tag>
                  <span class="pipeline-id">{{ wf.id }}</span>
                </div>
                <div class="pipeline-node-tags">
                  <el-tag :type="getCurrentTagType(wf)" size="mini">{{ getCurrentTagText(wf) }}</el-tag>
                  <el-tag :type="wfStateTagType(wf.wfState)" size="mini">{{ simplifiedStateText(wf.wfState) }}</el-tag>
                </div>
                <div class="pipeline-node-body">
                  <span>{{ (wf.merchantNum == null || wf.merchantNum === '') ? '-' : wf.merchantNum }}家商户</span>
                  <span class="node-sep">·</span>
                  <span>{{ (wf.storeNum == null || wf.storeNum === '') ? '-' : wf.storeNum }}家门店</span>
                </div>
              </div>
            </el-timeline-item>
          </el-timeline>
        </div>
      </div>

      <!-- 右侧详情 -->
      <div class="detail-panel">
        <el-tabs v-model="activeTab" type="border-card" class="detail-tabs" @tab-click="onTabClick">
          <!-- Tab 1: 审核记录 -->
          <el-tab-pane label="审核记录" name="audit" v-loading="loading.audit">
            <div v-if="auditTimelineItems.length > 0" style="padding: 20px;">
              <el-timeline>
                <el-timeline-item
                    v-for="(item, index) in auditTimelineItems"
                    :key="index"
                    :timestamp="item.time"
                    placement="top"
                    :color="getAuditTimelineColor(item)"
                >
                  <el-card shadow="never" style="border-left:3px solid #409EFF;">
                    <div style="display:flex; align-items:center; justify-content: space-between;">
                      <span style="font-weight:600;">{{ item.type }}</span>
                      <el-tag v-if="item.type" :type="item.state === '通过' ? 'success' : 'danger'" size="small">
                        {{ item.state }}
                      </el-tag>
                    </div>
                    <div class="audit-opinion-wrapper" style="margin:0 0 4px;color:#606266;font-size:13px;">
                      <div class="audit-opinion-content" :class="{ 'is-collapse': !secondAuditExpanded }">
                        审核意见：{{ item.desc || '' }}
                      </div>
                      <el-button v-if="(item.desc || '').length > 50" type="text" size="mini" @click="secondAuditExpanded = !secondAuditExpanded" style="flex-shrink:0; margin-left: 8px;">
                        {{ secondAuditExpanded ? '收起' : '展开' }}
                        <i :class="secondAuditExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      </el-button>
                    </div>
                  </el-card>
                </el-timeline-item>
              </el-timeline>
            </div>
            <el-empty v-else description="暂无审核记录" />
          </el-tab-pane>

          <!-- Tab 2: 资质文件 -->
          <el-tab-pane label="资质文件" name="files" v-loading="loading.files">
            <el-tree
              v-if="currentFiles.length > 0"
              :data="currentFiles"
              :props="{ children: 'children', label: 'label' }"
              default-expand-all
              node-key="id"
              highlight-current
              style="width: 100%; padding: 8px 0;"
              v-loading="loading.files"
            >
              <span class="custom-tree-node" slot-scope="{ node, data }">
                <span class="tree-node-label" :title="data.label">
                  <i :class="data.type === 'folder' ? 'el-icon-folder' : getFileIcon(data.extension)" class="tree-node-icon"></i>
                  {{ data.label }}
                </span>
                <span class="tree-node-actions" v-if="data.type === 'file'">
                  <el-button type="text" size="mini" @click.stop="previewFile(data)" v-if="data.extension === 'pdf'">预览</el-button>
                  <el-button type="text" size="mini" @click.stop="downloadFile(data)">下载</el-button>
                </span>
              </span>
            </el-tree>
            <el-empty v-else description="暂无数据" />
          </el-tab-pane>

          <!-- Tab 3: 上传记录 -->
          <el-tab-pane label="上传记录" name="upload" v-loading="loading.upload">
            <el-table v-if="uploadRecordList.length > 0" :data="uploadRecordList" border size="small" style="width:100%;" v-loading="loading.upload">
              <el-table-column type="index" label="序号" width="60" align="center" />
              <el-table-column prop="fileName" label="文件名称" min-width="140" show-overflow-tooltip />
<!--              <el-table-column prop="fileSize" label="文件大小" width="100" align="center" />-->
<!--              <el-table-column prop="fileType" label="文件类型" width="90" align="center">-->
<!--                <template slot-scope="scope">-->
<!--                  <el-tag size="mini">{{ scope.row.fileType }}</el-tag>-->
<!--                </template>-->
<!--              </el-table-column>-->
              <el-table-column prop="uploadDate" label="上传时间" width="140" align="center" />
              <el-table-column label="操作" width="100" align="center">
                <template slot-scope="scope">
                  <el-button type="text" size="small" @click="downloadFile(scope.row)">下载</el-button>
                </template>
              </el-table-column>
            </el-table>
            <el-empty v-else description="暂无数据" />
          </el-tab-pane>

          <!-- Tab 4: 关联商户 -->
          <el-tab-pane label="入库商户" name="merchant">
            <el-form :inline="true" size="mini" :model="merchantSearchForm" class="search-form">
              <el-form-item label="搜索词：">
                <el-input v-model="merchantSearchForm.keyword" clearable placeholder="请输入商户名称/信用代码" style="width:200px"/>
              </el-form-item>
              <el-form-item label="审核状态:">
                <el-select v-model="merchantSearchForm.status" placeholder="请选择" clearable style="width:140px">
                  <el-option value="reviewing" label="审核中" />
                  <el-option value="approve" label="通过" />
                  <el-option value="reject" label="不通过" />
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="merchantSearch">查询</el-button>
                <el-button @click="merchantReset">重置</el-button>
<!--                <el-button @click="goMerchantList" style="margin-left:auto;">查看全部</el-button>-->
              </el-form-item>
            </el-form>
            <el-table :data="merchantPagedData" border size="small" :height="merchantTableHeight" style="width:100%;" v-loading="loading.merchant">
              <el-table-column type="index" label="序号" width="60" align="center"/>
              <el-table-column prop="merchantName" label="商户名称" min-width="160" show-overflow-tooltip/>
              <el-table-column prop="merchantCreditCode" label="统一信用代码" min-width="160" show-overflow-tooltip/>
              <el-table-column prop="region" label="所属区域" width="100" align="center" show-overflow-tooltip/>
              <el-table-column prop="regLocation" label="注册地址" min-width="180" show-overflow-tooltip/>
              <el-table-column prop="auditState" label="审核状态" width="100" align="center">
                <template slot-scope="scope">
                  <el-tag :type="merchantAuditStateTagType(scope.row.auditState)" size="mini">
                    {{ merchantAuditStateText(scope.row.auditState) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="createTime" label="创建时间" width="160" align="center">
                <template slot-scope="scope">
                  {{ formatTime(scope.row.createTime) }}
                </template>
              </el-table-column>
            </el-table>
            <el-empty v-if="!loading.merchant && merchantTotal === 0" description="暂无商户数据"/>
            <div class="pagination">
              <el-pagination
                  @size-change="merchantSizeChange"
                  @current-change="merchantCurrentChange"
                  :current-page="merchantPage"
                  :page-sizes="[5, 10, 20]"
                  :page-size="merchantPageSize"
                  layout="total, sizes, prev, pager, next"
                  :total="merchantTotal"
              />
            </div>
          </el-tab-pane>

          <!-- Tab 5: 关联门店 -->
          <el-tab-pane label="入库门店" name="store">
            <el-form :inline="true" size="mini" :model="storeSearchForm" class="search-form">
              <el-form-item label="搜索词：">
                <el-input v-model="storeSearchForm.keyword" clearable placeholder="请输入门店名称/编号" style="width:160px"/>
              </el-form-item>
              <el-form-item label="审核状态:">
                <el-select v-model="storeSearchForm.status" placeholder="请选择" clearable style="width:140px">
                  <el-option value="reviewing" label="审核中" />
                  <el-option value="approve" label="通过" />
                  <el-option value="reject" label="不通过" />
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="storeSearch">查询</el-button>
                <el-button @click="storeReset">重置</el-button>
<!--                <el-button @click="goStoreList" style="margin-left:auto;">查看全部</el-button>-->
              </el-form-item>
            </el-form>
            <el-table :data="storePagedData" border size="small" :height="storeTableHeight" style="width:100%;" v-loading="loading.store">
              <el-table-column type="index" label="序号" width="60" align="center"/>
              <el-table-column prop="store_name" label="门店名称" min-width="120" show-overflow-tooltip/>
              <el-table-column prop="store_no" label="门店编码" width="80" align="center"/>
              <el-table-column prop="merchantName" label="所属商户" min-width="140" show-overflow-tooltip/>
              <el-table-column prop="businessAddress" label="经营地址" min-width="120" show-overflow-tooltip/>
              <el-table-column prop="region" label="所属地区" width="80" align="center"/>
              <el-table-column prop="auditState" label="审核状态" width="100" align="center">
                <template slot-scope="scope">
                  <el-tag :type="merchantAuditStateTagType(scope.row.auditState)" size="mini">
                    {{ merchantAuditStateText(scope.row.auditState) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="createTime" label="创建时间" width="160" align="center">
                <template slot-scope="scope">
                  {{ formatTime(scope.row.createTime) }}
                </template>
              </el-table-column>
            </el-table>
            <el-empty v-if="!loading.store && storeTotal === 0" description="暂无门店数据"/>
            <div class="pagination">
              <el-pagination
                  @size-change="storeSizeChange"
                  @current-change="storeCurrentChange"
                  :current-page="storePage"
                  :page-sizes="[10, 20, 50]"
                  :page-size="storePageSize"
                  layout="total, sizes, prev, pager, next"
                  :total="storeTotal"
              />
            </div>
          </el-tab-pane>

        </el-tabs>
      </div>
    </div>

<!--    <div class="form-footer-fixed">-->
<!--      <el-button size="medium" @click="goBack"><i class="el-icon-back"></i> 返回列表</el-button>-->
<!--    </div>-->
  </div>
</template>

<script>
import { getWorkflowListByParentId, getQualificationFiles, getUploadFileList, getAuditRecords, getMerchantsByFlowId, getStoresByFlowId } from "@/api/system/workflow/workflow";
import {SYS_APi_SCREENING_URL} from '@/utils/globals';
import { streamDownload } from '@/utils/download';

export default {
  name: "workflowView",
  data() {
    return {
      channelId: '',
      workflow: {},
      currentFlowId: '',
      activeTab: 'audit',
      tabLoaded: {}, // 记录各 tab 是否已加载
      workflowList: [],
      workflowFiles: [],
      workflowAuditRecords: [],
      uploadRecordList: [],
      auditTimelineItems: [],
      loading: { workflow:false, files: false, audit: false, merchant: false, store: false, upload: false },
      // 商户管理
      merchantSearchForm: { keyword: '', status: '' },
      listSearchForm: {},
      merchantPagedData: [],
      merchantTotal: 0,
      merchantPage: 1,
      merchantPageSize: 10,
      merchantTableHeight: 0,
      // 门店管理
      storeSearchForm: { keyword: '',  status: '' },
      storePagedData: [],
      storeTotal: 0,
      storePage: 1,
      storePageSize: 10,
      storeTableHeight: 0,
      firstAuditExpanded: false,
      secondAuditExpanded: false,
      wfTypeMap: {
        '1': '渠道遴选',
        '2': '新增商户/门店',
        '4': '修改商户/门店',
        '3': '退出商户/门店',
        '5': '渠道退出',
        '6': '支付业务许可证'
      }
    };
  },
  computed: {
    pipelineList() {
      if (!this.workflowList.length) return [];
      return this.workflowList.slice().sort((a, b) => b.createTime.localeCompare(a.createTime));
    },
    currentFiles() {
      return this.workflowFiles;
    },
    auditRecord() {
      if (!this.currentFlowId) return null;
      return this.workflowAuditRecords.find(r => r.id === this.currentFlowId) || null;
    }
  },
  created() {
    this.listSearchForm = {
      keyword: this.$route.query.keyword || '',
      wfType: this.$route.query.wfType || '',
      wfState: this.$route.query.wfState || '',
      startDate: this.$route.query.startDate || '',
      endDate: this.$route.query.endDate || '',
      channelId: this.$route.query.channelId || '',
      currentPage: this.$route.query.currentPage || 1,
      pageSize: this.$route.query.pageSize || 10
    };
  },
  mounted() {
    this.loadDetail();
    this.calcTableHeights();
    window.addEventListener('resize', this.calcTableHeights);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calcTableHeights);
  },
  methods: {
    loadDetail() {
      const id = this.$route.params.id;
      if (!id) {
        this.$message.error('流程ID不能为空');
        this.goBack();
        return;
      }

      this.loading.workflow = true;
      // 第一步：只加载左侧流程流水列表
      getWorkflowListByParentId({ parentId: id }).then(res => {
        if (res && res.code === '200') {
          var list = res.data || [];
          this.workflowList = list.slice().sort((a, b) => b.createTime.localeCompare(a.createTime));

          var flowId = list.some(w => w.id === id) ? id : (list[0] && list[0].id) || id;

          this.activeTab = 'audit';
          // 使用与流水列表相同的 flowId 加载审核记录
          this.loadTabData('audit', flowId);

          this.currentFlowId = flowId;
          this.workflow = this.workflowList.find(w => w.id === flowId) || this.workflowList[0] || {};
          this.channelId = this.workflow.channelId || '';

          this.merchantPage = 1;
          this.merchantSearchForm = { keyword: '', status: '' };
          this.storePage = 1;
          this.storeSearchForm = { keyword: '',  status: '' };
        } else {
          this.$message.error((res && res.message) || '加载流程流水失败');
          this.loading.workflow = false;
          this.goBack();
        }
      }).catch(() => {
        this.$message.error('加载流程流水失败，请稍后重试');
        this.loading.workflow = false;
        this.goBack();
      }).finally(() => {
        this.loading.workflow = false;
      });
    },

    loadTabData(tabName, flowId, force = false) {
      if (!force && this.tabLoaded[tabName]) {
        return;
      }

      this.tabLoaded[tabName] = true;

      const loaders = {
        audit: () => {
          this.loading.audit = true;
          return getAuditRecords({ workflowId: flowId }).then(res => {
            if (res && res.code === '200') {
              const dataList = res.data || [];
              // 假设 dataList 是数组，取第一个元素（因为传入 workflowId 应该只有一个）
              const auditData = dataList.length > 0 ? dataList[0] : null;
              if (auditData) {
                const timelineItems = [];
                // 处理初审列表
                const firstAuditList = auditData.firstAuditList || [];
                firstAuditList.forEach(item => {
                  // 判断初审状态：4->不通过，3或6->通过
                  let state = '';
                  if (item.audit_state === '4') {
                    state = '不通过';
                  } else if (item.audit_state === '3' || item.audit_state === '6') {
                    state = '通过';
                  }

                  timelineItems.push({
                    time: item.first_audit_time,
                    desc: item.first_audit_desc,
                    type: '初审',
                    state: state
                  });
                });
                // 处理终审列表
                const secondAuditList = auditData.secondAuditList || [];
                secondAuditList.forEach(item => {
                  // 判断终审状态：6->通过，7->不通过
                  let state = '';
                  if (item.audit_state === '6') {
                    state = '通过';
                  } else if (item.audit_state === '7') {
                    state = '不通过';
                  }

                  timelineItems.push({
                    time: item.second_audit_time,
                    desc: item.second_audit_desc,
                    type: '终审',
                    state: state
                  });
                });
                // 如果列表为空但 workflow 中有值，则添加 workflow 中的字段作为记录
                if (firstAuditList.length === 0 && auditData.workflow && auditData.workflow.firstAuditTime) {
                  // 判断初审状态：4->不通过，3或6->通过
                  let state = '';
                  if (auditData.workflow.wfState === '4') {
                    state = '不通过';
                  } else if (auditData.workflow.wfState === '3' || auditData.workflow.wfState === '6') {
                    state = '通过';
                  }
                  timelineItems.push({
                    time: auditData.workflow.firstAuditTime,
                    desc: auditData.workflow.firstAuditDesc,
                    type: '初审',
                    state: state
                  });
                } else if (auditData.workflow && auditData.workflow.firstAuditTime){
                  const exists = firstAuditList.some(item =>
                      (item.first_audit_desc || '').includes(auditData.workflow.firstAuditDesc)
                  );
                  if (!exists) {
                    // 判断初审状态：4->不通过，3或6->通过
                    let state = '';
                    if (auditData.workflow.wfState === '4') {
                      state = '不通过';
                    } else if (auditData.workflow.wfState === '3' || auditData.workflow.wfState === '6') {
                      state = '通过';
                    }
                    timelineItems.push({
                      time: auditData.workflow.firstAuditTime,
                      desc: auditData.workflow.firstAuditDesc,
                      type: '初审',
                      state: state
                    });
                  }
                }
                if (secondAuditList.length === 0 && auditData.workflow && auditData.workflow.secondAuditTime) {
                  // 判断终审状态：6->通过，7->不通过
                  let state = '';
                  if (auditData.workflow.wfState === '6') {
                    state = '通过';
                  } else if (auditData.workflow.wfState === '7') {
                    state = '不通过';
                  }
                  timelineItems.push({
                    time: auditData.workflow.secondAuditTime,
                    desc: auditData.workflow.secondAuditDesc,
                    type: '终审',
                    state: state
                  });
                } else if (auditData.workflow && auditData.workflow.secondAuditTime){
                  const exists = secondAuditList.some(item =>
                      (item.second_audit_desc || '').includes(auditData.workflow.secondAuditDesc)
                  );
                  if (!exists) {
                    let state = '';
                    if (auditData.workflow.wfState === '6') {
                      state = '通过';
                    } else if (auditData.workflow.wfState === '7') {
                      state = '不通过';
                    }
                    timelineItems.push({
                      time: auditData.workflow.secondAuditTime,
                      desc: auditData.workflow.secondAuditDesc,
                      type: '终审',
                      state: state
                    });
                  }
                }
                // 按时间排序
                timelineItems.sort((a, b) => (b.time || '').localeCompare(a.time || ''));
                // 每条记录独立的展开/收起状态
                this.auditTimelineItems = timelineItems.map(it => ({ ...it, expanded: false }));
              } else {
                this.auditTimelineItems = [];
              }
            } else {
              this.auditTimelineItems = [];
            }
          }).catch(() => { this.auditTimelineItems = []; }).finally(() => { this.loading.audit = false; });
        },
        files: () => {
          this.loading.files = true;
          return getQualificationFiles({ workflowId: flowId }).then(res => {
            if (res && res.code === '200') this.workflowFiles = res.data || [];
            else this.workflowFiles = [];
          }).catch(() => { this.workflowFiles = []; }).finally(() => { this.loading.files = false; });
        },
        upload: () => {
          this.loading.upload = true;
          return getUploadFileList({ workflowId: flowId }).then(res => {
            if (res && res.code === '200') this.uploadRecordList = res.data || [];
            else this.uploadRecordList = [];
          }).catch(() => { this.uploadRecordList = []; }).finally(() => { this.loading.upload = false; });
        },
        merchant: () => {
          this.loading.merchant = true;
          return getMerchantsByFlowId({ flowId, pageNo: this.merchantPage, pageSize: this.merchantPageSize })
              .then(res => {
                if (res && res.code === '200') {
                  this.merchantTotal = res.data && res.data.total || 0;
                  this.merchantPagedData = res.data && res.data.records || [];
                } else { this.merchantTotal = 0; this.merchantPagedData = []; }
              }).catch(() => { this.merchantTotal = 0; this.merchantPagedData = []; })
              .finally(() => { this.loading.merchant = false; });
        },
        store: () => {
          this.loading.store = true;
          return getStoresByFlowId({ flowId, pageNo: this.storePage, pageSize: this.storePageSize })
              .then(res => {
                if (res && res.code === '200') {
                  this.storeTotal = res.data && res.data.total || 0;
                  this.storePagedData = res.data && res.data.records || [];
                } else { this.storeTotal = 0; this.storePagedData = []; }
              }).catch(() => { this.storeTotal = 0; this.storePagedData = []; })
              .finally(() => { this.loading.store = false; });
        }
      };

      if (loaders[tabName]) {
        loaders[tabName]();
      }
    },

    onTabClick(tab) {
      const tabName = tab.paneName || (tab.props && tab.props.name);
      if (tabName) {
        this.loadTabData(tabName, this.currentFlowId, false);
      }
    },

    clearTabData() {
      this.workflowFiles = [];
      this.uploadRecordList = [];
      this.workflowAuditRecords = [];
      this.auditTimelineItems = [];
      this.merchantPagedData = [];
      this.merchantTotal = 0;
      this.storePagedData = [];
      this.storeTotal = 0;
    },

    selectFlow(flowId) {
      if (flowId === this.currentFlowId) return;
      this.currentFlowId = flowId;
      const wf = this.workflowList.find(item => item.id === flowId);
      if (!wf) return;
      this.workflow = { ...wf };

      this.activeTab = 'audit';
      this.tabLoaded = {};
      this.clearTabData();

      this.merchantPage = 1;
      this.merchantSearchForm = { keyword: '', status: '' };
      this.storePage = 1;
      this.storeSearchForm = { keyword: '',  status: '' };

      this.loadTabData('audit', flowId, true);
    },
    getAuditTimelineColor(type) {
      // type: 'first' 初审, 'second' 终审
      return type === 'first' ? '#409EFF' : '#67C23A';
    },
    getCurrentTagType(wf) {
      if (wf.id === this.$route.params.id) return 'primary';
      return 'warning';
    },
    getCurrentTagText(wf) {
      if (wf.id === this.$route.params.id) return '原流程';
      return '驳回重提';
    },
    simplifiedStateText(state) {
      if (state === '0') return '待提交';
      if (['1', '1-0', '1-1', '1-2', '2', '3', '5'].includes(state)) return '审核中';
      if (['4', '6','7', '4,6', '6,4', '4,7', '7,4', '6,7', '7,6'].includes(state)) return '审核完成';
      return '-';
    },
    wfStateTagTypeText(state) {
      if (['6'].includes(state)) return '通过';
      if (['4,6'].includes(state)) return '部分通过';
      if (['4', '7'].includes(state)) return '不通过';
      if (['1', '1-0', '1-1', '1-2', '3'].includes(state)) return '审核中';
      return 'info';
    },
    wfStateTagType(state) {
      if (['6', '4,6'].includes(state)) return 'success';
      if (['4', '7'].includes(state)) return 'danger';
      if (['1', '1-0', '1-1', '1-2', '3'].includes(state)) return 'warning';
      return 'info';
    },
    merchantAuditStateText(state) {
      if (['6'].includes(state)) return '通过';
      if (['4', '7'].includes(state)) return '不通过';
      if (['1', '1-0', '1-1', '1-2', '3'].includes(state)) return '审核中';
    },
    merchantAuditStateTagType(state) {
      if (['6'].includes(state)) return 'success';
      if (['4', '7'].includes(state)) return 'danger';
      if (['1', '1-0', '1-1', '1-2', '3'].includes(state)) return 'warning';
      return 'info';
    },
    goBack() { this.$router.push({ path: '/sys/workflowHistory/workflowList', query: this.listSearchForm }); },
    goMerchantList() { this.$router.push('/sys/workflow/merchantList/' + this.currentFlowId); },
    goStoreList() { this.$router.push('/sys/workflow/storeList/' + this.currentFlowId); },
    getFileIcon(ext) {
      const iconMap = {
        'pdf': 'el-icon-document',
        'xlsx': 'el-icon-tickets',
        'xls': 'el-icon-tickets',
        'doc': 'el-icon-document',
        'docx': 'el-icon-document',
        'zip': 'el-icon-folder-opened',
        'rar': 'el-icon-folder-opened'
      };
      return iconMap[ext] || 'el-icon-document';
    },
    /**
     * 格式化时间，去掉毫秒部分
     * @param {string} timeStr - 时间字符串，如 "2026-08-05 06:32:06.851"
     * @returns {string} - 格式化后的时间，如 "2026-08-05 06:32:06"
     */
    formatTime(timeStr) {
      if (!timeStr) return '-';
      // 取小数点前面的部分
      return timeStr.toString().split('.')[0];
    },
    previewFile(file) {
      if (!file.path) return;
      const loading = this.$loading({
        lock: true,
        text: '加载文件中...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });
      const token = this.$store.state.token;
      if (!token) {
        this.$message.error('未登录，无法预览');
        loading.close();
        return;
      }
      const apiUrl = SYS_APi_SCREENING_URL + '/filedown/bigFileDownLoad';
      const params = new URLSearchParams();
      params.append('url', file.path);
      params.append('name', file.label);
      const finalUrl = `${apiUrl}?${params.toString()}`;
      fetch(finalUrl, { headers: { 'token': token } })
          .then(res => {
            if (!res.ok) throw new Error(`服务器错误: ${res.status}`);
            return res.blob();
          })
          .then(blob => {
            loading.close();
            const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
            window.open(blobUrl, '_blank');
          })
          .catch(() => {
            loading.close();
            this.$message.error('预览失败');
          });
    },
    downloadFile(row) {
      const loading = this.$loading({
        lock: true,
        text: '正在下载...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });
      const apiUrl = SYS_APi_SCREENING_URL + '/filedown/bigFileDownLoad';
      const params = new URLSearchParams();
      params.append('url', row.path || row.filePath || '');
      params.append('name', row.label || row.fileName || 'download');
      const finalUrl = `${apiUrl}?${params.toString()}`;
      const token = this.$store.state.token;
      if (!token) {
        this.$message.error('未登录，无法下载');
        loading.close();
        return;
      }
      streamDownload(finalUrl, {
          token,
          filename: row.label || row.fileName || 'download',
          onProgress: (percent, downloadedMB) => {
            if (!percent) {
              loading.setText('正在下载...');
            } else if (downloadedMB < 1) {
              loading.setText(`正在下载 ${percent}% (${(downloadedMB * 1024).toFixed(0)} KB)...`);
            } else {
              loading.setText(`正在下载 ${percent}% (${downloadedMB.toFixed(1)} MB)...`);
            }
          },
        })
        .then(() => { this.$message.success('下载成功'); })
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.error(err);
            this.$message.error(err.message || '下载失败');
          }
        })
        .finally(() => { loading.close(); });
    },
    merchantFilter() {
      this.loading.merchant = true;
      getMerchantsByFlowId({
        flowId: this.currentFlowId,
        channelId: this.channelId,
        keyword: (this.merchantSearchForm.keyword || '').trim(),
        status: this.merchantSearchForm.status,
        pageNo: this.merchantPage,
        pageSize: this.merchantPageSize
      }).then(res => {
        if (res && res.code === '200') {
          this.merchantTotal = res.data && res.data.total || 0;
          this.merchantPagedData = res.data && res.data.records || [];
        } else {
          this.merchantTotal = 0;
          this.merchantPagedData = [];
        }
      }).catch(() => {
        this.merchantTotal = 0;
        this.merchantPagedData = [];
      }).finally(() => {
        this.loading.merchant = false;
      });
    },
    merchantSearch() { this.merchantPage = 1; this.merchantFilter(); },
    merchantReset() { this.merchantSearchForm = { keyword: '', status: '' }; this.merchantSearch(); },
    merchantSizeChange(val) { this.merchantPageSize = val; this.merchantFilter(); },
    merchantCurrentChange(val) { this.merchantPage = val; this.merchantFilter(); },

    storeFilter() {
      this.loading.store = true;
      getStoresByFlowId({
        flowId: this.currentFlowId,
        pageNo: this.storePage,
        pageSize: this.storePageSize,
        ...this.storeSearchForm
      }).then(res => {
        if (res && res.code === '200') {
          this.storeTotal = res.data && res.data.total || 0;
          this.storePagedData = res.data && res.data.records || [];
        } else {
          this.storeTotal = 0;
          this.storePagedData = [];
        }
      }).catch(() => {
        this.storeTotal = 0;
        this.storePagedData = [];
      }).finally(() => {
        this.loading.store = false;
      });
    },
    storeSearch() { this.storePage = 1; this.storeFilter(); },
    storeReset() { this.storeSearchForm = { keyword: '', status: '' }; this.storeSearch(); },
    storeSizeChange(val) { this.storePageSize = val; this.storeFilter(); },
    storeCurrentChange(val) { this.storePage = val; this.storeFilter(); },

    calcTableHeights() {
      this.$nextTick(() => {
        if (typeof window === 'undefined') return;
        const clientHeight = window.innerHeight || document.documentElement.clientHeight;
        const offset = 220;
        this.merchantTableHeight = Math.max(200, clientHeight - offset);
        this.storeTableHeight = Math.max(200, clientHeight - offset);
      });
    }
  }
};
</script>

<style scoped>
.flex-container { height: 100%; display: flex; flex-direction: column; text-align: left; }
.flex-header { flex-shrink: 0; background: #fff; padding-bottom: 12px; }
.flex-body { flex: 1; min-height: 0; display: flex; gap: 12px; padding: 12px 0; overflow: hidden; }

/* 顶部汇总 */
.channel-summary { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; background: #fff; border-radius: 8px; margin-top: 8px; border: 1px solid #ebeef5; }
.summary-left { flex: 1; }
.channel-name { font-size: 20px; font-weight: 600; color: #303133; margin: 0; }
.channel-meta { margin-top: 8px; font-size: 13px; color: #909399; }
.channel-meta span { margin-right: 24px; }
.summary-right { display: flex; gap: 32px; flex-shrink: 0; }
.stat-item { text-align: center; }
.stat-num { display: block; font-size: 28px; font-weight: 700; color: #409EFF; line-height: 1.2; }
.stat-label { display: block; font-size: 13px; color: #909399; margin-top: 2px; }

/* 左侧流程列表 */
.sidebar {
  width: 320px;
  flex-shrink: 0;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.sidebar-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  padding: 14px 16px 10px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  gap: 6px;
}
.sidebar-title::before {
  content: '';
  display: inline-block;
  width: 3px;
  height: 14px;
  background: #409EFF;
  border-radius: 2px;
}
.sidebar-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}
.sidebar-list::-webkit-scrollbar { width: 6px; }
.sidebar-list::-webkit-scrollbar-track { background: transparent; }
.sidebar-list::-webkit-scrollbar-thumb { background: #dcdfe6; border-radius: 3px; }
.pipeline-node {
  padding: 8px 12px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fafafa;
  cursor: pointer;
  transition: all 0.2s;
}
.pipeline-node:hover { border-color: #409EFF; background: #f0f7ff; }
.pipeline-node.is-current { border-color: #409EFF; background: #ecf5ff; box-shadow: 0 2px 8px rgba(64,158,255,0.12); }
.pipeline-node-top {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}
.pipeline-id-tag { flex-shrink: 0; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pipeline-node-tags { display: flex; align-items: center; gap: 4px; margin-top: 4px; }
.pipeline-node-body { display: flex; align-items: center; gap: 4px; font-size: 12px; color: #606266; margin-top: 4px; }
.node-sep { color: #c0c4cc; }
.pipeline-node-remark { margin-top: 4px; font-size: 11px; color: #e6a23c; font-style: italic; }

/* 右侧详情面板 */
.detail-panel {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.detail-tabs {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}
.detail-tabs >>> .el-tabs__content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}
.detail-tabs >>> .el-tabs__header {
  margin-bottom: 0;
  border-bottom: 1px solid #ebeef5;
  flex-shrink: 0;
}
.detail-tabs >>> .el-tab-pane {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.search-form { margin-bottom: 12px; }
.pagination { text-align: right; margin-top: 12px; }

/* 底部按钮 */
.form-footer-fixed { text-align: center; padding: 16px 0; border-top: 1px solid #ebeef5; background: #fff; flex-shrink: 0; }
.form-footer-fixed .el-button { min-width: 120px; }
/* 树形节点样式 */
.custom-tree-node { flex: 1; display: flex; align-items: center; justify-content: space-between; font-size: 13px; padding-right: 8px; }
.tree-node-label { display: flex; align-items: center; overflow: hidden; }
.tree-node-icon { margin-right: 4px; font-size: 14px; color: #409EFF; flex-shrink: 0; }
.tree-node-label i.el-icon-folder { color: #E6A23C; }
.tree-node-label i.el-icon-document { color: #F56C6C; }
.tree-node-label i.el-icon-tickets { color: #67C23A; }
.tree-node-actions { display: flex; gap: 4px; }

/* 审核意见展开/收起样式 */
.audit-opinion-content {
  flex: 1;
  min-width: 0;
  /* 容器只负责布局和截断 */
  word-break: break-all;
}

.text-inner {
  /* 核心样式写在这里，不管父容器怎么变，这里的样式永远不变 */
  color: #606266;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  display: block; /* 或者 inline */
}

.audit-opinion-content.is-collapse {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  white-space: normal; /* 截断模式下，修正 white-space */
}
</style>
